// src/components/CopySharedModal.js
// Modal for copying shared content to user's projects

import React, { useState, useEffect } from 'react';
import { X, FolderPlus, Search, Folder, AlertTriangle } from 'lucide-react';
import { copySharedItem } from '../services/enterpriseSharing';
import { searchProjects } from '../services/database';

/**
 * Helper function to compare style choices, with special handling for custom palettes.
 * It ignores the customPaletteId and compares the actual color values instead.
 * @param {object} source - The source project's styleChoices.
 * @param {object} target - The target project's styleChoices.
 * @returns {boolean} - True if styles are mismatched, false otherwise.
 */
const areStylesMismatched = (source, target) => {
  if (!source || !target) {
    return true; // Mismatched if either style object is missing.
  }

  const sourceCopy = { ...source };
  const targetCopy = { ...target };

  // If both are custom palettes, compare by colors, not by ID.
  if (sourceCopy.isCustomPalette && targetCopy.isCustomPalette) {
    // The ID will be different for the copied palette, so we remove it from the comparison.
    delete sourceCopy.customPaletteId;
    delete targetCopy.customPaletteId;
  }

  // Perform a deep comparison on the remaining properties.
  return JSON.stringify(sourceCopy) !== JSON.stringify(targetCopy);
};

const CopySharedModal = ({ item, onClose, onSuccess }) => {
  // State management
  const [copyMode, setCopyMode] = useState('new'); // 'new' or 'existing'
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [showStyleWarning, setShowStyleWarning] = useState(false);

  // Initialize new project title
  useEffect(() => {
    if (item) {
      setNewProjectTitle(item.itemTitle);
    }
  }, [item]);

  // Check for style mismatch when a project is selected
  useEffect(() => {
    const sourceStyles = item?.sourceStyleChoices;

    if (copyMode === 'existing' && selectedProject && sourceStyles) {
      const targetStyles = selectedProject.styleChoices || {};

      // Use the new, smarter comparison function
      if (areStylesMismatched(sourceStyles, targetStyles)) {
        setShowStyleWarning(true);
      } else {
        setShowStyleWarning(false);
      }
    } else {
      setShowStyleWarning(false);
    }
  }, [selectedProject, item, copyMode]);


  // Load user's projects when switching to existing mode
  useEffect(() => {
    if (copyMode === 'existing') {
      loadProjects();
    }
  }, [copyMode]);

  // Filter projects based on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProjects(projects);
      return;
    }

    const filtered = projects.filter(project =>
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    setFilteredProjects(filtered);
  }, [searchTerm, projects]);

  // Load user's projects
  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const userProjects = await searchProjects('', 'dateDesc');
      setProjects(userProjects);
      setFilteredProjects(userProjects);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load your projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  // Handle copy action
  const handleCopy = async () => {
    if (copyMode === 'new' && !newProjectTitle.trim()) {
      setError('Project title is required');
      return;
    }

    if (copyMode === 'existing' && !selectedProject) {
      setError('Please select a project');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let result;
      if (copyMode === 'new') {
        result = await copySharedItem(
          item.shareId,
          null,
          newProjectTitle.trim()
        );
      } else {
        result = await copySharedItem(
          item.shareId,
          selectedProject._id,
          null
        );
      }

      onSuccess(result);
    } catch (err) {
      console.error('Error copying shared item:', err);
      setError(err.message || 'Failed to copy shared content');
    } finally {
      setLoading(false);
    }
  };

  // Format date for project display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      {/* MODIFIED: Added flex flex-col to the modal container */}
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* MODIFIED: Added flex-shrink-0 to the header */}
        <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold">
            Copy "{item.itemTitle}" to Your Projects
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* MODIFIED: Added flex-1 and overflow-y-auto to the main content area */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Copy Mode Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              How would you like to add this content?
            </label>
            
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="copyMode"
                  value="new"
                  checked={copyMode === 'new'}
                  onChange={(e) => setCopyMode(e.target.value)}
                  className="mr-3"
                />
                <FolderPlus className="w-4 h-4 mr-2 text-gray-500" />
                Create new project
              </label>
              
              <label className="flex items-center">
                <input
                  type="radio"
                  name="copyMode"
                  value="existing"
                  checked={copyMode === 'existing'}
                  onChange={(e) => setCopyMode(e.target.value)}
                  className="mr-3"
                />
                <Folder className="w-4 h-4 mr-2 text-gray-500" />
                Add to existing project
              </label>
            </div>
          </div>

          {/* New Project Mode */}
          {copyMode === 'new' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Project Title
              </label>
              <input
                type="text"
                value={newProjectTitle}
                onChange={(e) => setNewProjectTitle(e.target.value)}
                placeholder="Enter project title"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                maxLength={100}
              />
              <p className="text-xs text-gray-500 mt-1">
                A new project will be created with this shared content as the first view.
              </p>
            </div>
          )}

          {/* Existing Project Mode */}
          {copyMode === 'existing' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Project
              </label>
              
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search your projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                {loadingProjects ? (
                  <div className="p-4 text-center text-gray-500">
                    Loading your projects...
                  </div>
                ) : filteredProjects.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {projects.length === 0 ? (
                      "You don't have any projects yet"
                    ) : (
                      "No projects match your search"
                    )}
                  </div>
                ) : (
                  filteredProjects.map((project) => (
                    <button
                      key={project._id}
                      onClick={() => setSelectedProject(project)}
                      className={`w-full text-left p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors ${
                        selectedProject?._id === project._id ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <Folder className="w-4 h-4 mr-3 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">
                            {project.title}
                          </h4>
                          <p className="text-sm text-gray-500 truncate">
                            {project.description || 'No description'}
                          </p>
                          <p className="text-xs text-gray-400">
                            Created {formatDate(project.createdAt)}
                          </p>
                        </div>
                        {selectedProject?._id === project._id && (
                          <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
              
              {showStyleWarning && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-yellow-800 text-sm flex items-start">
                  <AlertTriangle className="w-5 h-5 mr-3 flex-shrink-0 text-yellow-500" />
                  <div>
                    <span className="font-semibold">Style Mismatch:</span> The copied view uses different device, color, or font settings. This may cause inconsistent styling. For best results, use a matching project or select “Create a New Project.”
                  </div>
                </div>
              )}

              {selectedProject && !showStyleWarning && (
                <p className="text-xs text-gray-500 mt-2">
                  The shared content will be added as a new view in "{selectedProject.title}".
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* MODIFIED: Added flex-shrink-0 to the footer container */}
        <div className="p-4 border-t flex-shrink-0">
            <div className="flex justify-end space-x-3">
                <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                Cancel
                </button>
                
                <button
                onClick={handleCopy}
                disabled={
                    loading || 
                    (copyMode === 'new' && !newProjectTitle.trim()) ||
                    (copyMode === 'existing' && !selectedProject)
                }
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                {loading ? 'Copying...' : copyMode === 'new' ? 'Create Project' : 'Add to Project'}
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default CopySharedModal;